/**
 * E-PR (docs/fibtec-etlgen-e-pr-design-spec.md §4) and the installation-ownership fix
 * (docs/fibtec-etlgen-github-install-ownership-fix-design.md) — the two GitHub redirects that
 * finish "Connect GitHub", one component on two routes, told apart by their query parameters:
 *
 * 1. `/github/callback` is the App's "Setup URL". GitHub sends the browser here after the user
 *    installs the App, with `installation_id`/`setup_action`/`state`. That id is not trusted
 *    (it can be spoofed), so this page does not post it: it asks the API for the github.com
 *    authorize URL for that installation and sends the browser there.
 * 2. `/github/authorized` is the App's "Callback URL". GitHub sends the browser back here after
 *    the user authorizes the App, with `code` and `state` (or `error` if they declined). This
 *    page posts those to POST /github/install-callback, which accepts the installation only if
 *    that user can access it.
 *
 * Both pages still carry the user's own Cognito session (normal page loads in the same browser),
 * so the API calls attach the usual JWT.
 *
 * This is always reached as the far end of the popup DeliverAsPr.tsx's "Connect GitHub" opens
 * (`window.open(..., '_blank')`) — a bug found live: the original "Back to the generator" link
 * navigated *this same popup tab* to "/", spawning a second, unrelated portal session in it
 * instead of returning the user to the one they actually came from (their generated script,
 * filled-in delivery form, all still sitting untouched in the original tab). Closing the popup is
 * what actually does that, so that's what this page does — automatically a moment after success,
 * and via an explicit button either way, in case the browser blocks the automatic close (some
 * browsers only allow a script to close a tab it opened, which holds here, but not universally,
 * and not for a window.close() called too long after load in every browser).
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ApiError, getGithubAuthorizeUrl, submitGithubInstallCallback } from '../api/client';

type Status = 'pending' | 'success' | 'error';

function apiMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

export function GithubCallback() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState<Status>('pending');
  const [message, setMessage] = useState('');

  // Which of the two redirects this is: the second one carries a `code` (or GitHub's `error`).
  const authorizing = !params.has('code') && !params.has('error');

  useEffect(() => {
    const fail = (text: string) => {
      setStatus('error');
      setMessage(text);
    };

    if (!authorizing) {
      const code = params.get('code');
      const state = params.get('state');
      if (params.has('error') || !code || !state) {
        fail('GitHub authorization was cancelled or returned an incomplete response.');
        return;
      }
      void submitGithubInstallCallback(code, state)
        .then(() => setStatus('success'))
        .catch((err: unknown) => fail(apiMessage(err, 'Could not confirm the GitHub connection.')));
      return;
    }

    const installationId = Number(params.get('installation_id'));
    if (params.get('setup_action') === 'cancel') {
      fail('GitHub installation was cancelled or returned an incomplete response.');
      return;
    }
    if (params.get('setup_action') === 'request') {
      fail(
        'The installation is waiting for a GitHub organisation owner to approve it. ' +
          'Once they have, connect GitHub again.',
      );
      return;
    }
    if (!Number.isInteger(installationId) || installationId <= 0) {
      fail('GitHub installation was cancelled or returned an incomplete response.');
      return;
    }

    void getGithubAuthorizeUrl(installationId)
      .then(({ authorize_url }) => window.location.assign(authorize_url))
      .catch((err: unknown) => fail(apiMessage(err, 'Could not start the GitHub confirmation.')));
    // Runs once per page load — the query params this effect reads
    // never change without a fresh navigation here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status !== 'success') return;
    // A brief pause so the success message is actually readable before
    // the tab vanishes, not an instant, jarring close.
    const timer = window.setTimeout(() => window.close(), 1200);
    return () => window.clearTimeout(timer);
  }, [status]);

  return (
    <main className="page">
      <h1>Connecting GitHub</h1>
      {status === 'pending' && (
        <p className="muted">
          {authorizing
            ? 'Sending you to GitHub to confirm the installation is yours…'
            : 'Confirming your installation…'}
        </p>
      )}
      {status === 'success' && (
        <p className="alert alert--info">
          GitHub is connected. You can deliver jobs as pull requests now — this tab will close
          automatically.
        </p>
      )}
      {status === 'error' && (
        <p className="alert alert--error" role="alert">
          {message}
        </p>
      )}
      <button className="btn btn--primary" onClick={() => window.close()}>
        Close this tab
      </button>
      <p className="muted small">
        Your generator session is in the other tab — it hasn't changed.
      </p>
    </main>
  );
}
